import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, Upload, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

export default function PhotoRequiredModal({ onClose, language = "fr" }) {
  const navigate = useNavigate();

  const handleAddPhoto = () => {
    onClose?.();
    navigate("/profile/edit");
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-card border border-white/10 rounded-3xl max-w-sm w-full overflow-hidden shadow-2xl"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-pink-500/20 to-purple-500/20 p-6 text-center">
            <div className="w-20 h-20 rounded-full bg-pink-500/20 border-2 border-dashed border-pink-500 flex items-center justify-center mx-auto mb-4">
              <Camera className="w-10 h-10 text-pink-500" />
            </div>

            <h2 className="text-xl font-bold text-white mb-2">
              {language === "fr"
                ? "Photo de profil requise"
                : "Profile photo required"}
            </h2>
            <p className="text-muted-foreground text-sm">
              {language === "fr"
                ? "Ajoutez une photo pour interagir avec les autres membres"
                : "Add a photo to interact with other members"}
            </p>
          </div>

          {/* Benefits list */}
          <div className="p-4 space-y-3">
            <div className="flex items-start gap-3 p-3 bg-white/5 rounded-xl">
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-green-500">✓</span>
              </div>
              <div>
                <p className="text-white font-medium text-sm">
                  {language === "fr" ? "Liker des profils" : "Like profiles"}
                </p>
                <p className="text-muted-foreground text-xs">
                  {language === "fr"
                    ? "Montrez votre intérêt aux autres"
                    : "Show your interest to others"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-white/5 rounded-xl">
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-green-500">✓</span>
              </div>
              <div>
                <p className="text-white font-medium text-sm">
                  {language === "fr" ? "Recevoir des matchs" : "Get matches"}
                </p>
                <p className="text-muted-foreground text-xs">
                  {language === "fr"
                    ? "Connectez-vous avec vos matchs"
                    : "Connect with your matches"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-white/5 rounded-xl">
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-green-500">✓</span>
              </div>
              <div>
                <p className="text-white font-medium text-sm">
                  {language === "fr" ? "Envoyer des messages" : "Send messages"}
                </p>
                <p className="text-muted-foreground text-xs">
                  {language === "fr"
                    ? "Démarrez des conversations"
                    : "Start conversations"}
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="p-4 pt-0 space-y-3">
            <Button
              onClick={handleAddPhoto}
              className="w-full h-12 bg-primary text-white font-bold rounded-xl"
            >
              <Upload className="w-5 h-5 mr-2" />
              {language === "fr" ? "Ajouter ma photo" : "Add my photo"}
            </Button>
            
            <button
              onClick={onClose}
              className="w-full text-muted-foreground text-sm hover:text-white transition-colors"
            >
              {language === "fr" ? "Plus tard" : "Later"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Hook to check if user has a photo
export function usePhotoRequired(user) {
  const hasPhoto = user?.main_photo || user?.photos?.length > 0 || user?.picture?.includes('googleusercontent');
  return !hasPhoto;
}
